import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  INCLUB_APP_STORE_BADGE_URL,
  INCLUB_APP_STORE_URL,
  INCLUB_PLAY_STORE_BADGE_URL,
  INCLUB_PLAY_STORE_URL
} from '@shared/constants/inclub-app.constants';

const YOUTUBE_URL_ID_REGEX =
  /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/|v\/))([a-zA-Z0-9_-]{11})/;

interface IdentityValidationStep {
  id: number;
  title: string;
  description: string;
  active: boolean;
}

@Component({
  selector: 'app-identity-validation-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './identity-validation-modal.component.html',
  styleUrls: ['./identity-validation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IdentityValidationModalComponent implements OnChanges, AfterViewChecked, OnDestroy {
  @ViewChild('identityDialog')
  private readonly identityDialog?: ElementRef<HTMLDialogElement>;

  @Input() open = false;
  @Input() closable = false;

  @Input() youtubeVideoUrl = 'https://youtu.be/b4Magy1wLp4';

  @Input() title = '¿Cómo realizar mi validación facial?';

  @Input() downloadTitle = 'Descarga la App Inclub';

  @Input() footerText = 'Valida tu identidad en pocos minutos.';

  @Input() appStoreUrl = INCLUB_APP_STORE_URL;

  @Input() playStoreUrl = INCLUB_PLAY_STORE_URL;

  readonly appStoreBadgeUrl = INCLUB_APP_STORE_BADGE_URL;

  readonly playStoreBadgeUrl = INCLUB_PLAY_STORE_BADGE_URL;

  readonly validationSteps: readonly IdentityValidationStep[] = [
    {
      id: 1,
      title: 'Identidad digital',
      description: 'Escanea tu documento de identidad para extraer tus datos por ti.',
      active: true,
    },
    {
      id: 2,
      title: 'Validación facial',
      description: 'Tu selfie debe coincidir con la de tu documento de identidad más reciente.',
      active: false,
    },
    {
      id: 3,
      title: 'Firma y activación',
      description: 'Verifica tu correo y número de celular, así como registrar tu firma digital.',
      active: false,
    },
  ];

  @Output() openChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();
  @Output() paymentRequested = new EventEmitter<void>();
  @Output() networkRequested = new EventEmitter<void>();
  @Output() logoutRequested = new EventEmitter<void>();

  readonly titleId = 'identity-validation-modal-title';
  readonly descriptionId = 'identity-validation-modal-description';

  safeYoutubeEmbedUrl: SafeResourceUrl | null = null;
  private readonly isBrowser: boolean;

  private scrollLocked = false;
  private previousBodyOverflow = '';
  private previousBodyPaddingRight = '';
  private previousDocumentOverflow = '';

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly sanitizer: DomSanitizer,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.syncYoutubeEmbedUrl();
  }

  get videoTitle(): string {
    return `Video: ${this.title}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['youtubeVideoUrl']) {
      this.syncYoutubeEmbedUrl();
    }

    if (changes['open']) {
      if (!this.open) {
        this.closeNativeDialog();
      }

      this.syncDocumentScroll();
    }
  }

  ngAfterViewChecked(): void {
    this.openNativeDialog();
  }

  ngOnDestroy(): void {
    this.closeNativeDialog();
    this.unlockDocumentScroll();
  }

  close(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.closable) return;

    this.closeNativeDialog();
    this.open = false;
    this.openChange.emit(false);
    this.closed.emit();

    this.syncDocumentScroll();
  }

  requestLogout(): void {
    this.logoutRequested.emit();
  }

  requestPayment(): void {
    this.paymentRequested.emit();
  }

  requestNetwork(): void {
    this.networkRequested.emit();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  trackByStepId(_: number, step: IdentityValidationStep): number {
    return step.id;
  }

  private openNativeDialog(): void {
    const dialog = this.identityDialog?.nativeElement;

    if (!this.isBrowser || !this.open || !dialog?.isConnected || dialog.open) return;

    dialog.showModal();
  }

  private closeNativeDialog(): void {
    const dialog = this.identityDialog?.nativeElement;

    if (!dialog?.open) return;

    dialog.close();
  }

  private syncDocumentScroll(): void {
    if (this.open) {
      this.lockDocumentScroll();
      return;
    }

    this.unlockDocumentScroll();
  }

  private lockDocumentScroll(): void {
    if (!this.isBrowser || this.scrollLocked) return;

    const body = this.document.body;
    const html = this.document.documentElement;
    const scrollbarWidth = Math.max(window.innerWidth - html.clientWidth, 0);

    this.previousBodyOverflow = body.style.overflow;
    this.previousBodyPaddingRight = body.style.paddingRight;
    this.previousDocumentOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    this.scrollLocked = true;
  }

  private unlockDocumentScroll(): void {
    if (!this.isBrowser || !this.scrollLocked) return;

    const body = this.document.body;
    const html = this.document.documentElement;

    body.style.overflow = this.previousBodyOverflow;
    body.style.paddingRight = this.previousBodyPaddingRight;
    html.style.overflow = this.previousDocumentOverflow;

    this.scrollLocked = false;
  }

  private syncYoutubeEmbedUrl(): void {
    const videoId = this.extractYoutubeVideoId(this.youtubeVideoUrl);

    this.safeYoutubeEmbedUrl = videoId
      ? this.buildTrustedYoutubeEmbedUrl(videoId)
      : null;
  }

  private extractYoutubeVideoId(value?: string | null): string | null {
    const rawValue = this.normalizeText(value);
    if (!rawValue) return null;

    const directId = this.cleanYoutubeId(rawValue);
    if (directId) return directId;

    try {
      const normalizedUrl = /^https?:\/\//i.test(rawValue) ? rawValue : `https://${rawValue}`;
      const url = new URL(normalizedUrl);

      const hostname = url.hostname.replace(/^www\./, '').replace(/^m\./, '');

      if (hostname === 'youtu.be') {
        const id = url.pathname.split('/').find(Boolean);
        return this.cleanYoutubeId(id);
      }

      if (!hostname.endsWith('youtube.com')) return null;

      const videoIdFromQuery = this.cleanYoutubeId(url.searchParams.get('v'));
      if (videoIdFromQuery) return videoIdFromQuery;

      const segments = url.pathname.split('/').filter(Boolean);
      const supportedPaths = ['embed', 'shorts', 'live', 'v'];

      for (const path of supportedPaths) {
        const index = segments.indexOf(path);

        if (index >= 0) {
          return this.cleanYoutubeId(segments[index + 1]);
        }
      }

      return null;
    } catch {
      const match = YOUTUBE_URL_ID_REGEX.exec(rawValue);
      return this.cleanYoutubeId(match?.[1]);
    }
  }

  private cleanYoutubeId(value?: string | null): string | null {
    const normalizedValue = this.normalizeText(value)
      .replace(/[?&].*$/, '')
      .replace(/\/$/, '');

    return /^[a-zA-Z0-9_-]{11}$/.test(normalizedValue) ? normalizedValue : null;
  }

  private buildTrustedYoutubeEmbedUrl(videoId: string): SafeResourceUrl | null {
    const safeVideoId = this.cleanYoutubeId(videoId);
    if (!safeVideoId) return null;

    const embedUrl = new URL(`https://www.youtube-nocookie.com/embed/${safeVideoId}`);
    embedUrl.searchParams.set('autoplay', '0');
    embedUrl.searchParams.set('rel', '0');
    embedUrl.searchParams.set('controls', '1');
    embedUrl.searchParams.set('disablekb', '1');
    embedUrl.searchParams.set('fs', '0');
    embedUrl.searchParams.set('iv_load_policy', '3');
    embedUrl.searchParams.set('playsinline', '1');

    const allowedHosts = new Set(['www.youtube-nocookie.com', 'youtube-nocookie.com']);
    if (embedUrl.protocol !== 'https:') return null;
    if (!allowedHosts.has(embedUrl.hostname)) return null;
    if (embedUrl.pathname !== `/embed/${safeVideoId}`) return null;

    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl.toString());
  }

  private normalizeText(value?: string | null): string {
    return typeof value === 'string' ? value.trim() : '';
  }
}
